import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingBag, Truck, ShieldCheck, HeartHandshake, Sparkles, IndianRupee, MapPin } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="max-w-5xl mx-auto px-4 md:px-6 pt-16 md:pt-20">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-3 py-1 text-xs uppercase tracking-wider">
          <Sparkles className="w-3 h-3" /> Our story
        </div>
        <h1 className="text-4xl md:text-6xl font-display font-semibold mt-4 max-w-3xl leading-[1.05]">
          Your <span className="italic text-accent">neighbourhood shop</span>, upgraded for the phone in your pocket.
        </h1>
        <p className="text-lg text-muted-foreground mt-5 max-w-2xl">
          Babu Baazar started with a simple belief: the fastest, freshest groceries shouldn&rsquo;t come from a warehouse across town — they should come from the shop down your street.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-6 py-12">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Truck, title: "30-minute delivery", body: "Rides that start from the neighbourhood store closest to you." },
            { icon: ShieldCheck, title: "100% authentic", body: "Direct from Babu Store — no third-party warehouse, no swaps, no surprises." },
            { icon: HeartHandshake, title: "Fair to shopkeepers", body: "Your local store keeps a fair share on every order. Growth for them, not just for us." },
          ].map((f, i) => (
            <Card key={i} className="card-soft border-transparent">
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent grid place-items-center mb-3">
                  <f.icon className="w-5 h-5" />
                </div>
                <div className="font-display text-xl">{f.title}</div>
                <p className="text-sm text-muted-foreground mt-1">{f.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-6 py-12 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <h2 className="text-3xl md:text-4xl font-display font-semibold">Built for how India actually shops</h2>
          <p className="text-muted-foreground mt-4">
            Cash on delivery. GST-compliant invoices. Full order tracking. Hindi + regional support (coming soon). Everything a real customer of a real Indian kirana needs — with none of the friction of a chain store app.
          </p>
        </div>
        <div className="rounded-3xl bg-gradient-to-br from-accent/10 via-secondary to-primary/10 p-8">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              { icon: IndianRupee, k: "Cash on delivery", v: "Always" },
              { icon: MapPin, k: "Local coverage", v: "5 km radius" },
              { icon: ShieldCheck, k: "GST invoices", v: "Every order" },
              { icon: Truck, k: "Delivery in", v: "~30 mins" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl bg-card p-4">
                <s.icon className="w-4 h-4 text-accent mb-1.5" />
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.k}</div>
                <div className="text-lg font-display font-semibold">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center mx-auto mb-4">
          <ShoppingBag className="w-6 h-6" />
        </div>
        <h2 className="text-3xl md:text-4xl font-display font-semibold">Ready to shop?</h2>
        <p className="text-muted-foreground mt-2">First order onwards, fresh groceries in under 30 minutes.</p>
        <Button asChild size="lg" className="btn-primary-glow mt-5" data-testid="about-shop-btn">
          <Link to="/shop">Browse products</Link>
        </Button>
      </section>
    </div>
  );
}
